import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Users, Briefcase, Zap } from 'lucide-react';
import { fleet as mockFleet } from '../../mock/data';
import { RevealText, Reveal } from '../motion/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.76, 0, 0.24, 1];
const pad = (n) => String(n).padStart(2, '0');

const slide = {
  enter: (dir) => ({ clipPath: dir > 0 ? 'inset(0% 0% 0% 100%)' : 'inset(0% 100% 0% 0%)', scale: 1.15 }),
  center: { clipPath: 'inset(0% 0% 0% 0%)', scale: 1 },
  exit: (dir) => ({ clipPath: dir > 0 ? 'inset(0% 100% 0% 0%)' : 'inset(0% 0% 0% 100%)', scale: 1.05 }),
};

const useFleet = () => {
  const [list, setList] = useState(mockFleet);
  useEffect(() => {
    axios.get(`${API}/fleet`).then((r) => r.data.vehicles?.length && setList(r.data.vehicles)).catch(() => {});
  }, []);
  return list;
};

const ArrowBtn = ({ onClick, label, testId, children }) => (
  <button type="button" onClick={onClick} aria-label={label} data-testid={testId}
    className="h-12 w-12 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors duration-300">
    {children}
  </button>
);

const Details = ({ v }) => (
  <motion.div key={v.id} initial="hidden" animate="visible" exit={{ opacity: 0, y: -20, transition: { duration: 0.3 } }}
    variants={{ visible: { transition: { staggerChildren: 0.08, delayChildren: 0.25 } } }} data-testid="fleet-showcase-details">
    {[
      <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 flex items-center gap-2">{v.category}{v.isElectric && <><span className="h-px w-4 bg-white/30" /><Zap className="h-3 w-3" /> Electric</>}</p>,
      <h3 className="text-3xl lg:text-5xl font-bold tracking-tight mt-3" data-testid="fleet-showcase-name">{v.name}</h3>,
      <div className="flex items-center gap-8 py-5 my-6 border-y border-white/10 text-sm uppercase tracking-wider text-white/70">
        <span className="flex items-center gap-2"><Users className="h-4 w-4" strokeWidth={1.25} />{v.passengers || v.capacity} Passengers</span>
        {v.luggage && <span className="flex items-center gap-2"><Briefcase className="h-4 w-4" strokeWidth={1.25} />{v.luggage} Luggage</span>}
      </div>,
      <p className="text-white/60 leading-relaxed line-clamp-4">{v.description}</p>,
      <div className="flex flex-wrap gap-3 mt-8">
        <Link to="/booking" className="px-6 py-3 bg-white text-black text-sm font-semibold hover:bg-gray-200 transition-colors" data-testid="fleet-showcase-book">Book this vehicle</Link>
        <Link to="/fleet" className="px-6 py-3 border border-white/40 text-sm font-semibold hover:bg-white hover:text-black transition-colors" data-testid="fleet-showcase-details-link">Full details</Link>
      </div>,
    ].map((el, i) => (
      <motion.div key={i} variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } } }}>{el}</motion.div>
    ))}
  </motion.div>
);

export const FleetShowcase = () => {
  const vehicles = useFleet();
  const [[index, dir], setState] = useState([0, 0]);
  const total = vehicles.length;
  const go = (d) => setState(([i]) => [(i + d + total) % total, d]);
  const jump = (i) => setState(([cur]) => [i, i > cur ? 1 : -1]);
  const v = vehicles[index] || vehicles[0];

  return (
    <section className="relative bg-black text-white py-24 lg:py-32 overflow-hidden outline-none focus-visible:ring-1 focus-visible:ring-white/60 focus-visible:ring-inset" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'ArrowRight') go(1); if (e.key === 'ArrowLeft') go(-1); }} data-testid="home-fleet">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4">The Fleet</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight"><RevealText text="Arrive in something exceptional" /></h2>
          </div>
          <div className="flex items-center gap-5">
            <span className="font-mono text-sm text-white/60 tabular-nums" data-testid="fleet-counter">{pad(index + 1)} / {pad(total)}</span>
            <ArrowBtn onClick={() => go(-1)} label="Previous vehicle" testId="fleet-prev"><ArrowLeft className="h-5 w-5" /></ArrowBtn>
            <ArrowBtn onClick={() => go(1)} label="Next vehicle" testId="fleet-next"><ArrowRight className="h-5 w-5" /></ArrowBtn>
          </div>
        </div>

        <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <motion.div className="lg:col-span-7 relative aspect-[16/10] overflow-hidden bg-neutral-900 cursor-grab active:cursor-grabbing touch-pan-y"
            drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.25}
            onDragEnd={(_, info) => { if (info.offset.x < -70) go(1); else if (info.offset.x > 70) go(-1); }} data-testid="fleet-showcase-stage">
            <AnimatePresence initial={false} custom={dir}>
              <motion.img key={v.id} src={v.image} alt={v.name} custom={dir} variants={slide} initial="enter" animate="center" exit="exit"
                transition={{ duration: 1.1, ease: EASE }} className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
            </AnimatePresence>
            <span className="absolute top-4 right-5 text-8xl font-bold leading-none text-transparent [-webkit-text-stroke:1px_rgba(255,255,255,0.55)] pointer-events-none">{pad(index + 1)}</span>
            <span className="absolute bottom-4 left-5 text-[10px] uppercase tracking-[0.3em] text-white/60 pointer-events-none">Drag or use the arrows</span>
          </motion.div>
          <div className="lg:col-span-5 min-h-[360px]">
            <AnimatePresence mode="wait"><Details v={v} /></AnimatePresence>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-3 md:grid-cols-6 gap-3" data-testid="fleet-thumbs">
          {vehicles.map((x, i) => (
            <button key={x.id} type="button" onClick={() => jump(i)} className="group text-left" data-testid={`fleet-thumb-${i}`} aria-label={`Show ${x.name}`}>
              <div className={`aspect-[16/10] overflow-hidden transition-opacity duration-300 ${i === index ? 'opacity-100' : 'opacity-40 group-hover:opacity-80'}`}>
                <img src={x.image} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="mt-2 h-px bg-white/15 relative overflow-hidden">
                {i === index && <motion.div layoutId="fleet-thumb-bar" className="absolute inset-0 bg-white" />}
              </div>
              <p className={`mt-2 text-[11px] uppercase tracking-wider truncate ${i === index ? 'text-white' : 'text-white/40'}`}>{x.name}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
